import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { googleLogout } from "@react-oauth/google";
import { TOKEN, AUTH_USER, IS_SUPER_ADMIN } from "../store/selectors";
import {
  loginWithGoogle as loginWithGoogleService,
  getAuthMe,
  logout as logoutService,
} from "../services/auth/auth.service";
import { mapAuthMeToUserEntity } from "../services/auth/auth.mapper";
import {
  getToken,
  getRefreshToken,
  storeTokens,
  clearTokens,
} from "../services/api";
import { Role } from "../types";
import { registerBackOfficeContext, resetBackOfficeAnalytics } from "../lib/posthog";

interface LoginResult {
  success: boolean;
  error?: string;
  message?: string;
}

// Frozen copy (SCRUM-706). Used when the backend's own message cannot be
// read, so an authorized-looking failure never turns into a blank error.
export const NO_ACCESS_MESSAGE =
  "No tienes acceso a Admin Emmi. Contacta a un administrador para solicitar acceso.";

export const useAuth = () => {
  const token = useSelector(TOKEN);
  const user = useSelector(AUTH_USER);
  const isSuperAdmin = useSelector(IS_SUPER_ADMIN);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const dispatch = useDispatch();

  // Hydrates Redux from GET /auth/me instead of decoding the (now opaque)
  // access token. No client-side exp check: a stale-but-refreshable token
  // is recovered transparently by the shared `api` instance's single-flight
  // refresh interceptor on the resulting 401; only a hard refresh failure
  // (or a non-ADMIN role) clears the local session here.
  useEffect(() => {
    const hydrate = async () => {
      const storedToken = getToken();

      if (!storedToken) {
        setLoadingAuth(false);
        return;
      }

      try {
        const authMe = await getAuthMe();

        if (authMe.role !== Role.ADMIN) {
          console.warn(
            "Usuario sin permisos de administrador, removiendo token"
          );
          clearTokens();
          dispatch({ type: "auth/cleanAuth" });
          return;
        }

        const authUser = mapAuthMeToUserEntity(authMe);

        dispatch({
          type: "auth/setAuth",
          payload: {
            token: storedToken,
            user: authUser,
            isSuperAdmin: authMe.is_super_admin,
          },
        });

        registerBackOfficeContext(authUser);
      } catch (error) {
        console.error("No se pudo recuperar la sesión:", error);
        clearTokens();
        dispatch({ type: "auth/cleanAuth" });
      } finally {
        setLoadingAuth(false);
      }
    };

    hydrate();
  }, [dispatch]);

  /**
   * Signs in with the Google id_token minted by Google Identity Services.
   * The email is never sent: the backend reads it from the token it verifies,
   * and answers 403 when that address is not on the Admin Emmi allowlist.
   */
  const loginWithGoogle = async (idToken: string): Promise<LoginResult> => {
    setIsLoading(true);
    try {
      const response = await loginWithGoogleService(idToken);

      // Defense in depth: /auth/google only ever returns ADMIN, but a
      // regression there must not silently open the back-office.
      if (response.role !== Role.ADMIN) {
        return {
          success: false,
          error: "access_denied",
          message: NO_ACCESS_MESSAGE,
        };
      }

      storeTokens(response.accessToken, response.refreshToken);

      const authMe = await getAuthMe();
      const authUser = mapAuthMeToUserEntity(authMe);

      dispatch({
        type: "auth/setAuth",
        payload: {
          token: response.accessToken,
          user: authUser,
          isSuperAdmin: authMe.is_super_admin,
        },
      });

      registerBackOfficeContext(authUser);

      return { success: true };
    } catch (error: any) {
      // Covers both a rejected sign-in and a failed post-login /auth/me
      // hydration — in the latter case tokens were already stored, so roll
      // them back rather than leaving an unhydrated session.
      clearTokens();

      const status = error?.response?.status;
      const backendMessage = error?.response?.data?.message;

      // 403 is the allowlist rejection: show the server's copy, falling back
      // to the frozen one. Anything else is a genuine failure.
      if (status === 403) {
        return {
          success: false,
          error: "access_denied",
          message: backendMessage || NO_ACCESS_MESSAGE,
        };
      }

      return {
        success: false,
        error: "login_error",
        message:
          backendMessage || "No se pudo iniciar sesión. Intenta nuevamente.",
      };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        await logoutService(refreshToken);
      }
    } catch (error) {
      // Best-effort: local session always clears regardless of the
      // backend call's outcome.
      console.error("Error al cerrar sesión en el servidor:", error);
    } finally {
      clearTokens();
      resetBackOfficeAnalytics();
      // Tells Google Identity Services to stop auto-selecting this account,
      // so "cerrar sesión" does not immediately offer to sign the same user
      // back in. No-op when the GIS script never loaded.
      googleLogout();
      dispatch({ type: "auth/cleanAuth" });
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    loadingAuth,
    token,
    user,
    isSuperAdmin,
    isAuthenticated: !!token,
    logout,
    loginWithGoogle,
  };
};
